import { Card } from "@/components/ui/card";
import { Calendar, MapPin, Plane } from "lucide-react";

const FlightCardLoadingSkeleton = () => {
  return (
    <Card className="@container/flight-card min-w-0 border rounded-md p-2 shadow-sm">
      <div className="flex min-w-0 gap-3 my-1 px-1">
        <div className="w-full min-w-0 flex-1">
          {/* Flight header with flight number */}
          <div className="flex items-center gap-1.5 mb-2 w-full justify-between">
            <div className="flex items-center gap-1.5">
              {/* Flight badge skeleton */}
              <div className="h-5 w-16 rounded-full bg-muted animate-pulse"></div>

              {/* Airline info skeleton */}
              <div className="flex items-center gap-1.5">
                <div className="h-5 w-5 rounded-full bg-muted animate-pulse"></div>
                <div className="h-3 w-12 @min-[400px]/flight-card:w-20 rounded-md bg-muted animate-pulse"></div>
                <div className="h-3 w-12 rounded-md bg-muted animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Flight journey skeleton */}
          <div className="space-y-3">
            {[1, 2].map((_, index) => (
              <div key={index} className="relative">
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(56px,0.65fr)_minmax(0,1fr)] gap-1">
                  {/* Departure skeleton */}
                  <div className="flex min-w-0 flex-1 items-start gap-2">
                    <div className="hidden @min-[800px]/flight-card:flex">
                      <div className="relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border bg-muted animate-pulse">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted/50">
                          <Plane className="h-3 w-3 text-muted-foreground/50" />
                        </div>
                      </div>
                    </div>

                    <div className="flex min-w-0 flex-col">
                      <div className="flex flex-wrap items-baseline gap-1.5">
                        <div className="h-5 w-12 rounded-md bg-muted animate-pulse"></div>
                        <div className="h-4 w-8 rounded-md bg-muted animate-pulse"></div>
                      </div>

                      <div className="flex items-center gap-1 mt-1">
                        <Calendar className="h-3 w-3 text-muted-foreground/50" />
                        <div className="h-3 w-12 @min-[400px]/flight-card:w-20 rounded-md bg-muted animate-pulse"></div>
                      </div>

                      <div className="mt-1 hidden @min-[400px]/flight-card:flex items-start gap-1">
                        <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground/50" />
                        <div className="h-3 w-12 @min-[640px]/flight-card:w-24 rounded-md bg-muted animate-pulse"></div>
                      </div>
                    </div>
                  </div>

                  {/* Flight duration skeleton */}
                  <div className="my-1 flex flex-1 flex-col items-center justify-center px-1">
                    <div className="h-4 w-12 rounded-full bg-muted animate-pulse"></div>

                    {/* Flight path animation */}
                    <div className="my-2 flex w-full items-center justify-center relative">
                      <div className="h-[1px] w-full bg-muted/50"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-1.5 w-1.5 rounded-full bg-muted animate-pulse"></div>
                      </div>
                    </div>

                    <div className="h-4 w-12 rounded-full bg-muted animate-pulse"></div>
                  </div>

                  {/* Arrival skeleton */}
                  <div className="flex min-w-0 flex-1 items-start justify-end gap-2">
                    <div className="flex min-w-0 flex-col items-end">
                      <div className="flex flex-wrap items-baseline gap-1.5">
                        <div className="h-5 w-12 rounded-md bg-muted animate-pulse"></div>
                        <div className="h-4 w-8 rounded-md bg-muted animate-pulse"></div>
                      </div>

                      <div className="flex items-center gap-1 mt-1 justify-end w-full">
                        <Calendar className="h-3 w-3 text-muted-foreground/50" />
                        <div className="h-3 w-12 @min-[400px]/flight-card:w-20 rounded-md bg-muted animate-pulse"></div>
                      </div>

                      <div className="mt-1 hidden @min-[400px]/flight-card:flex items-start gap-1 justify-end w-full">
                        <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground/50" />
                        <div className="h-3 w-12 @min-[640px]/flight-card:w-24 rounded-md bg-muted animate-pulse"></div>
                      </div>
                    </div>
                    <div className="hidden @min-[800px]/flight-card:flex">
                      <div className="relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border bg-muted animate-pulse">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted/50">
                          <Plane className="h-3 w-3 rotate-90 text-muted-foreground/50" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Price details skeleton for desktop */}
        <div className="hidden @min-[640px]/flight-card:flex shrink-0 items-center justify-end bg-muted/30 px-1.5 pb-1.5 pt-1 rounded-md w-36 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute -right-4 -top-4 h-8 w-8 rounded-full bg-muted/50"></div>
          <div className="absolute -bottom-4 -left-4 h-8 w-8 rounded-full bg-muted/50"></div>
          <div className="w-full">
            <div className="h-3 w-12 ml-auto rounded-md bg-muted animate-pulse mb-1"></div>
            <div className="h-5 w-16 ml-auto rounded-md bg-muted animate-pulse"></div>

            <div className="mt-2 h-7 w-full rounded-full bg-muted animate-pulse"></div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 px-1 @min-[640px]/flight-card:hidden" aria-hidden="true">
        <div className="h-4 w-12 animate-pulse rounded bg-muted" />
        <div className="ml-auto h-5 w-20 animate-pulse rounded bg-muted" />
        <div className="h-8 w-16 animate-pulse rounded-full bg-muted" />
      </div>
    </Card>
  );
};

export { FlightCardLoadingSkeleton };
